import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase F.6 iter128 fix — Module-level singleton state for the global axios interceptor.
// Lives outside the React tree so it survives re-mounts and can be read by the interceptor
// callback before the component state is wired up.
let _setNetworkErrorRef = null;
let _interceptorInstalled = false;
const _looksLikeHtml = (data, headers) => {
  try {
    const ct = (headers?.["content-type"] || headers?.["Content-Type"] || "").toLowerCase();
    if (ct.includes("text/html")) return true;
    if (typeof data === "string" && data.trim().startsWith("<")) return true;
  } catch (_e) { /* response shape unreadable — bail */ }
  return false;
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  
  // Preview mode state - allows managers to see app as different roles
  const [previewMode, setPreviewMode] = useState(null); // null | "employee" | "quoter" | "client"

  // Phase 19.27 — Platform admins get a different default portal. They can flip on
  // "View as Customer" to see the regular customer modules (Quotes / Clients / etc).
  // Persisted to localStorage so a hard refresh keeps the toggle state.
  const [adminViewAsCustomer, setAdminViewAsCustomerState] = useState(() => {
    try { return localStorage.getItem("bg.adminViewAsCustomer") === "1"; } catch { return false; }
  });
  const setAdminViewAsCustomer = (v) => {
    setAdminViewAsCustomerState(!!v);
    try { localStorage.setItem("bg.adminViewAsCustomer", v ? "1" : "0"); } catch {}
  };

  // Phase F.6 iter127 — Network-error banner state. Set when the API returns HTML
  // instead of JSON (DNS misroute / proxy interception). Surfaces as a visible
  // banner so users can self-diagnose instead of seeing a silent blank screen.
  const [networkError, setNetworkError] = useState(null);

  // Wire the module-level setter so the global axios interceptor can update React state.
  _setNetworkErrorRef = setNetworkError;

  // Install ONE global axios response interceptor (idempotent across re-mounts).
  // Catches both success (200 OK with text/html — Cloudflare Pages SPA fallback) and
  // error cases. Throws a `NETWORK_MISROUTE` error so calling code's catch block runs.
  useEffect(() => {
    if (_interceptorInstalled) return;
    _interceptorInstalled = true;
    axios.interceptors.response.use(
      (response) => {
        try {
          const url = response?.config?.url || "";
          if (url.startsWith(API) && _looksLikeHtml(response?.data, response?.headers)) {
            const e = new Error("NETWORK_MISROUTE");
            e.code = "NETWORK_MISROUTE";
            e.userMessage =
              "We can't reach the Business Genie API from your network. " +
              "Try mobile data or change your DNS to 1.1.1.1 / 8.8.8.8 and reload.";
            if (_setNetworkErrorRef) {
              _setNetworkErrorRef({
                kind: "html_response",
                host: API,
                message: e.userMessage,
              });
            }
            return Promise.reject(e);
          }
        } catch (_int_err) { /* never block a successful response on interceptor errors */ }
        return response;
      },
      (error) => {
        try {
          const url = error?.config?.url || "";
          // iter141 — Normalize FastAPI/Pydantic validation errors so that
          // `error.response.data.detail` is always a string. When the backend
          // hasn't intercepted a 422 (rare regression / non-standard route),
          // detail can be an array of {loc,msg,type,input,url} objects, and
          // downstream code that renders it inside <Toast>/<Alert> triggers
          // React error #31 ("Objects are not valid as a React child").
          try {
            const detail = error?.response?.data?.detail;
            if (Array.isArray(detail)) {
              error.response.data.detail = detail
                .map((d) => {
                  if (typeof d === "string") return d;
                  if (d && typeof d === "object") {
                    const loc = Array.isArray(d.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
                    const msg = d.msg || d.message || "Invalid input";
                    return loc ? `${loc}: ${msg}` : msg;
                  }
                  return String(d);
                })
                .join(" · ");
            } else if (detail && typeof detail === "object" && !Array.isArray(detail)) {
              error.response.data.detail = detail.msg || detail.message || JSON.stringify(detail);
            }
          } catch (_e) { /* leave detail untouched */ }
          if (
            url.startsWith(API) &&
            _looksLikeHtml(error?.response?.data, error?.response?.headers)
          ) {
            error.code = "NETWORK_MISROUTE";
            error.userMessage =
              "We can't reach the Business Genie API from your network. " +
              "Try mobile data or change your DNS to 1.1.1.1 / 8.8.8.8 and reload.";
            if (_setNetworkErrorRef) {
              _setNetworkErrorRef({
                kind: "html_response",
                host: API,
                message: error.userMessage,
              });
            }
          }
        } catch (_int_err) { /* never break the original error path */ }
        return Promise.reject(error);
      }
    );
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // The global axios interceptor (installed in the effect above) detects HTML-misroute
  // responses on BOTH 2xx and error paths and rejects them with error.code='NETWORK_MISROUTE'.
  // So here we only need to distinguish that code from "real" auth failures.
  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setNetworkError(null);
    } catch (error) {
      if (error?.code === "NETWORK_MISROUTE") {
        // Interceptor has already set networkError state; do NOT clear the token
        // so the user can retry after fixing their network.
        console.warn("Auth fetch hit a network misroute — token preserved.");
      } else {
        console.error("Failed to fetch user:", error);
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const data = response.data;

      // Check if 2FA is required - don't set auth yet
      if (data.requires_2fa) {
        return data;
      }

      const { access_token, user: userData } = data;
      localStorage.setItem("token", access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      setNetworkError(null);
      return userData;
    } catch (err) {
      // The global axios interceptor tags HTML-misroute errors with NETWORK_MISROUTE.
      // We just rethrow — the Login page checks for this code to render a clear toast.
      throw err;
    }
  };

  // Method to set auth after 2FA verification
  const setAuth = (accessToken, userData) => {
    localStorage.setItem("token", accessToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(userData);
  };

  const register = async (name, email, password, role = "employee") => {
    const response = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
      role,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setPreviewMode(null);
  };

  // Preview mode functions
  const startPreview = (role) => {
    if (user?.role === "office_manager") {
      setPreviewMode(role);
    }
  };

  const exitPreview = () => {
    setPreviewMode(null);
  };

  // Effective role considers preview mode
  const effectiveRole = previewMode || user?.role;
  
  const isOfficeManager = previewMode ? false : user?.role === "office_manager";
  // Phase 15: 'quoter' is a permission, not a role. Preview mode "quoter" lets managers test the experience.
  const isQuoter = previewMode === "quoter"
    ? true
    : (previewMode ? false : !!user?.can_create_quotes);
  const isEmployee = effectiveRole === "employee";
  const isClient = effectiveRole === "client";
  
  // Actual role (ignores preview mode) - for showing preview controls
  const actualRole = user?.role;
  const canPreview = actualRole === "office_manager";

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        token,
        loading,
        login,
        register,
        logout,
        setAuth,
        refreshUser: fetchUser,
        isOfficeManager,
        isQuoter,
        isEmployee,
        isClient,
        isAuthenticated: !!user,
        // Preview mode
        previewMode,
        startPreview,
        exitPreview,
        canPreview,
        actualRole,
        effectiveRole,
        // Platform admin view toggle (hide customer modules by default for platform admins)
        adminViewAsCustomer,
        setAdminViewAsCustomer,
        isPlatformAdminMode: !!user?.is_platform_admin && !adminViewAsCustomer && !previewMode,
        // Phase F.6 iter127 — network-error surface for the global banner
        networkError,
        clearNetworkError: () => setNetworkError(null),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
